import { Know, KnowDocumentPages } from 'interfaces/Know'
import { useState, useRef, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import theme from 'stories/utils/theme'
import NextImage from 'components/elements/NextImage'
import { Body2 } from 'stories/elements/Typography/Text'
import NewButton from 'components/elements/NewButton'
import _useTranslation from 'lib/hooks/_useTranslation'

// Import arrow SVGs
import LeftArrow from './left-know-carousel-arrow.svg'
import RightArrow from './right-know-carousel-arrow.svg'
import { trackEvent } from 'lib/tracking/analytics-service'
import {
    AnalyticsEventKnowPageCarouselImageClick,
    AnalyticsEventKnowPageCarouselImageImpression,
} from 'lib/constants/AnalyticsEventType'
import { appDeepLinkKnow } from 'lib/features/app-deep-links'
import { redirectToAppDownloadUrlAsync } from 'lib/features/app-links'

const CarouselContainer = styled.div`
    position: relative;
    width: 100%;
    background: transparent;
    overflow: hidden;
`

const CarouselContent = styled.div`
    display: flex;
    width: 100%;
    overflow-x: auto;
    scroll-behavior: smooth;
    scrollbar-width: none;
    -ms-overflow-style: none;
    scroll-snap-type: x mandatory;
    padding: 0 3px;
    scroll-padding-left: -10px;

    &::before {
        content: '';
        flex: 0 0 42px;
        min-width: 42px;
    }

    &::after {
        content: '';
        flex: 0 0 42px;
        min-width: 42px;
    }

    &::-webkit-scrollbar {
        display: none;
    }
`

const PageItem = styled.div`
    flex-shrink: 0;
    width: calc(100% - 30px);
    position: relative;
    scroll-snap-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2.5px;
    margin: 0 2px;

    max-width: 500px;

    .image-container {
        width: 100%;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;

        img {
            width: 100%;
            height: auto;
            max-height: 700px;
            object-fit: contain;
            border-radius: 15px;
            transition: opacity 0.3s ease;
            filter: blur(1px);
            -webkit-filter: blur(1px);
        }
    }

    .view-button-overlay {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 5;
        transition: opacity 0.3s ease;

        div {
            min-width: 160px;

            /* Responsive button sizing */
            @media (min-width: 768px) {
                min-width: 180px;
            }

            @media (min-width: 1024px) {
                min-width: 200px;
            }
        }

        p {
            font-family: var(--font-inter);
            font-weight: 650;
            letter-spacing: 0.5px;
            font-size: 18px !important;
            color: ${theme.colors.white};
            padding: 0 12px;

            /* Responsive text sizing */
            @media (min-width: 768px) {
                font-size: 19px !important;
                padding: 0 16px;
            }

            @media (min-width: 1024px) {
                font-size: 20px !important;
                padding: 0 20px;
            }
        }
    }

    &:not(.center-page) {
        .image-container img {
            opacity: 0.8;
        }
        .view-button-overlay {
            opacity: 0.7;
        }
    }
`

const NavigationContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    padding: 15px;
    padding-bottom: 50px;

    /* Better spacing on larger screens */
    @media (min-width: 768px) {
        gap: 30px;
        padding: 20px;
        padding-bottom: 40px;
    }

    @media (min-width: 1024px) {
        gap: 40px;
        padding: 25px;
        padding-bottom: 35px;
    }
`

const ArrowButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    width: 36px;
    height: fit-content;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s ease, transform 0.2s ease;

    &:hover:not(:disabled) {
        opacity: 0.8;
        transform: scale(1.1);
    }

    &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
    }

    /* Larger buttons on bigger screens */
    @media (min-width: 768px) {
        width: 40px;
    }

    @media (min-width: 1024px) {
        width: 44px;
    }
`

const PageCounter = styled.div`
    color: ${theme.colors.grayScaleSecondary};
    font-family: var(--font-inter);
    font-size: 14px;
    font-weight: 400;
    padding: 8px 16px;
    letter-spacing: 0.5px;

    /* Slightly larger and better spaced on larger screens */
    @media (min-width: 768px) {
        font-size: 15px;
        padding: 10px 20px;
    }

    @media (min-width: 1024px) {
        font-size: 16px;
        padding: 12px 24px;
    }
`

interface Props {
    know: Know
    knowPages: KnowDocumentPages[]
    referrerScreen?: string
}

const KnowPagesCarousel = ({ knowPages, know, referrerScreen }: Props) => {
    const [currentIndex, setCurrentIndex] = useState(0)
    const carouselRef = useRef<HTMLDivElement>(null)
    const isProgrammaticScrolling = useRef(false)
    const { t: tCommon } = _useTranslation('common')

    // Helper function to get current item width based on screen size
    const getItemWidth = () => {
        if (!carouselRef.current) return 0

        const containerWidth = carouselRef.current.clientWidth
        const screenWidth = window.innerWidth

        // Match the CSS media queries
        if (screenWidth >= 1200) {
            // Large desktop: width: calc(50% - 30px), max-width: 400px
            return Math.min(containerWidth * 0.5 - 30, 400) + 4 // +4 for margins (2px each side)
        } else if (screenWidth >= 1024) {
            // Desktop: width: calc(60% - 30px), max-width: 450px
            return Math.min(containerWidth * 0.6 - 30, 450) + 4
        } else if (screenWidth >= 768) {
            // Tablet: width: calc(80% - 30px), max-width: 500px
            return Math.min(containerWidth * 0.8 - 30, 500) + 4
        } else {
            // Mobile: width: calc(100% - 30px)
            return containerWidth - 30 + 4
        }
    }

    const scrollToIndex = useCallback((index: number) => {
        if (!carouselRef.current) return

        // Set flag to prevent scroll handler from interfering during programmatic scroll
        isProgrammaticScrolling.current = true

        const itemWidth = getItemWidth()
        const scrollLeft = index * itemWidth
        carouselRef.current.scrollTo({
            left: scrollLeft,
            behavior: 'smooth',
        })

        // Clear flag after animation completes (smooth scroll typically takes ~300-500ms)
        setTimeout(() => {
            isProgrammaticScrolling.current = false
        }, 600)
    }, [])

    const handlePrevious = () => {
        const newIndex = Math.max(0, currentIndex - 1)
        setCurrentIndex(newIndex)
        scrollToIndex(newIndex)
    }

    const handleNext = () => {
        const newIndex = Math.min(knowPages.length - 1, currentIndex + 1)
        setCurrentIndex(newIndex)
        scrollToIndex(newIndex)
    }

    const handleConversionPageClick = (index: number) => {
        trackEvent(AnalyticsEventKnowPageCarouselImageClick, {
            know,
            useBeacon: true,
            referrerScreen,
            valueId: index,
        })
        const downloadDeepLinkPath = appDeepLinkKnow(know.uuid)
        redirectToAppDownloadUrlAsync('dynamic', 'know-mobile-ai-enhanced-hero', downloadDeepLinkPath)
    }

    useEffect(() => {
        trackEvent(AnalyticsEventKnowPageCarouselImageImpression, {
            know,
            useBeacon: true,
            referrerScreen,
            valueId: currentIndex,
        })
    }, [currentIndex, know, referrerScreen])

    useEffect(() => {
        const handleScroll = () => {
            // Ignore scroll events during programmatic scrolling (arrow navigation)
            if (isProgrammaticScrolling.current || !carouselRef.current) return

            const scrollLeft = carouselRef.current.scrollLeft
            const itemWidth = getItemWidth()
            const newIndex = Math.round(scrollLeft / itemWidth)
            if (newIndex !== currentIndex && newIndex >= 0 && newIndex < knowPages.length) {
                setCurrentIndex(newIndex)
            }
        }

        const carousel = carouselRef.current
        if (carousel) {
            carousel.addEventListener('scroll', handleScroll, { passive: true })
            return () => carousel.removeEventListener('scroll', handleScroll)
        }
    }, [currentIndex, knowPages.length, know, referrerScreen])

    // Handle resize events to recalculate scroll position
    useEffect(() => {
        const handleResize = () => {
            // Recalculate and maintain current position after resize
            setTimeout(() => {
                // Temporarily set flag to prevent scroll handler interference during resize repositioning
                isProgrammaticScrolling.current = true
                scrollToIndex(currentIndex)
            }, 100) // Small delay to allow DOM to update
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [currentIndex, scrollToIndex])

    if (!knowPages.length) {
        return null
    }

    return (
        <CarouselContainer>
            <CarouselContent ref={carouselRef}>
                {knowPages.map((page, index) => (
                    <PageItem
                        key={page.uuid}
                        className={index === currentIndex ? 'center-page' : ''}
                        onClick={() => handleConversionPageClick(index)}
                    >
                        <div className="image-container">
                            <NextImage
                                width={400}
                                height={560}
                                src={page.imageUrl}
                                alt={`Page ${page.pageNumber}`}
                                loading={index === 0 ? 'eager' : 'lazy'}
                                priority={index === 0}
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: '700px',
                                    objectFit: 'contain',
                                }}
                            />
                        </div>
                        <div className="view-button-overlay">
                            <NewButton
                                isWebapp
                                width="fit-content"
                                height={50}
                                onClick={() => handleConversionPageClick(index)}
                            >
                                <Body2 color={theme.colors.white} fontFamily="var(--font-inter)">
                                    {tCommon('common/view')}
                                </Body2>
                            </NewButton>
                        </div>
                    </PageItem>
                ))}
            </CarouselContent>

            <NavigationContainer>
                <ArrowButton onClick={handlePrevious} disabled={currentIndex === 0} aria-label="Previous page">
                    <LeftArrow />
                </ArrowButton>

                <PageCounter>
                    {currentIndex + 1} / {knowPages.length}
                </PageCounter>

                <ArrowButton onClick={handleNext} disabled={currentIndex >= knowPages.length - 1} aria-label="Next page">
                    <RightArrow />
                </ArrowButton>
            </NavigationContainer>
        </CarouselContainer>
    )
}

export default KnowPagesCarousel
